import { VERIFICATION_STAGES_WITH_NOTIFICATION } from '@/consts/forms/verification';
import { useVerificationInfo } from '@/hooks/queries';
import { IAllAccountData } from '@/types';
import {
    VERIFICATION_PERSONAL_DETAILS_NOTIFICATION,
    VERIFICATION_PROFESSIONAL_DETAILS_NOTIFICATION,
} from '@/consts/forms/verification';
import { AccountTabs, VerificationInfo } from '@/types';
import { createArrayElementChecker } from '@/utils/fp';
import { config } from '@/config';

const requestInfoByAccountSection = {
    [AccountTabs.PersonalDetails]: VERIFICATION_PERSONAL_DETAILS_NOTIFICATION,
    [AccountTabs.ProfessionalDetails]: VERIFICATION_PROFESSIONAL_DETAILS_NOTIFICATION,
};

/* Remove personal details notification if both auto and manual verification options are false */
const filterPersonalDetailsNotification = (
    sectionsWithVerificationNotifications: AccountTabs[],
    verificationInfo: VerificationInfo,
): AccountTabs[] => {
    if (
        config.appEnv !== 'production' &&
        !verificationInfo.isAutoIdentityVerificationEnabled &&
        !verificationInfo.isManualIdentityVerificationEnabled
    ) {
        return sectionsWithVerificationNotifications.filter(
            (section) => section !== AccountTabs.PersonalDetails,
        );
    }
    return sectionsWithVerificationNotifications;
};

export const useVerificationNotification = (accountData: IAllAccountData | undefined) => {
    const hasVerificationNotification = VERIFICATION_STAGES_WITH_NOTIFICATION.includes(
        accountData?.verificationStageId || 0,
    );
    const shouldUseVerificationInfo = Boolean(
        accountData?.verificationRequestToken && hasVerificationNotification,
    );
    const { data: verificationInfo } = useVerificationInfo(
        accountData?.verificationRequestToken ?? '',
        shouldUseVerificationInfo,
    );

    const defaultValues = {
        sectionsWithVerificationNotifications: [] as AccountTabs[],
        isAutoIdentityVerificationEnabled: verificationInfo?.isAutoIdentityVerificationEnabled,
        isManualIdentityVerificationEnabled: verificationInfo?.isManualIdentityVerificationEnabled,
    };

    const getVerificationSectionWithNotification = (
        verificationInfo: VerificationInfo | undefined,
    ) => {
        if (!verificationInfo) {
            return defaultValues;
        }

        const { requestedInfo } = verificationInfo;
        let sectionsWithVerificationNotifications = Object.entries(requestInfoByAccountSection)
            .filter(([_, sectionRequestedInfo]) => {
                const doesArrayShareSectionRequestedInfo =
                    createArrayElementChecker(sectionRequestedInfo);
                return doesArrayShareSectionRequestedInfo(requestedInfo);
            })
            .map(([section]) => section as AccountTabs);

        sectionsWithVerificationNotifications = filterPersonalDetailsNotification(
            sectionsWithVerificationNotifications,
            verificationInfo,
        );

        return {
            ...defaultValues,
            sectionsWithVerificationNotifications,
        };
    };

    return getVerificationSectionWithNotification(verificationInfo);
};
